import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../js/hooks/useAuth";
import { useNotifications } from "../../js/hooks/useNotifications";
import { deskRoleFromPath, messagesPath } from "../../js/utils/messagingPaths";
import { formatDate } from "../../js/utils/formatters";

function noticeLabel(item) {
  if (item.type === "export") return "A communication snapshot was saved.";
  if (item.type === "archive") return "A file was moved to the archive.";
  if (item.type === "inquiry") return `${item.payload?.from_name || "A colleague"} sent an office inquiry.`;
  if (item.type === "system") return "A system notice is waiting.";
  if (item.type === "calendar") return "A calendar reminder is due.";
  return "A new note is waiting.";
}

export default function NotificationBell() {
  const { session, role } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { items, unread, markRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    function onPointer(event) {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onPointer);
    return () => document.removeEventListener("mousedown", onPointer);
  }, []);

  if (!session?.userId) return null;

  async function openNotice(item) {
    await markRead([item.id]);
    setOpen(false);
    if (item.type === "system") {
      navigate("/notices");
      return;
    }
    const desk = deskRoleFromPath(pathname, role);
    const archived = item.type === "archive";
    const office = item.type === "inquiry";
    navigate(
      messagesPath(desk, {
        office,
        archive: archived,
        threadId: item.payload?.thread_id,
        transactionId: item.payload?.thread_id ? undefined : item.payload?.transaction_id,
      })
    );
  }

  return (
    <div className="notice-bell" ref={rootRef}>
      <button
        type="button"
        className="notice-bell__button"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((current) => !current)}
      >
        Inbox alerts
        {unread ? (
          <span className="unread-pip" aria-live="polite">
            {unread}
          </span>
        ) : null}
      </button>
      {open ? (
        <div className="notice-bell__panel" role="menu">
          {items.length === 0 ? (
            <p className="muted">No notices yet.</p>
          ) : (
            <ul>
              {items.slice(0, 8).map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    className={`notice-bell__item${!item.is_read ? " is-unread" : ""}`}
                    onClick={() => openNotice(item)}
                  >
                    <span>{noticeLabel(item)}</span>
                    <time>{formatDate(item.created_at)}</time>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
